// File: frontend/src/components/marketing/visuals/MarketingMobileVisual.js
//
// Responsive mobile-device marketing visual: a generic phone frame containing a
// compact PesaFii interface (header, revenue summary, router status, active
// users, recent transactions). Fictional data; no API calls. A sample
// notification slides in once in view (skipped under reduced motion).

import React, { useEffect, useState } from "react";
import { Box, Typography, Stack, Divider, useTheme, alpha } from "@mui/material";
import { motion, useReducedMotion } from "framer-motion";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import { MarketingStatusBadge, MarketingMobileFrame, MarketingMiniBars } from "./primitives";
import { useInViewOnce, useCountUp } from "../motion";
import { DEMO_METRICS, DEMO_WALLET, DEMO_TRANSACTIONS, DEMO_LOCATIONS, fmtUGX } from "../../../data/marketingDemoData";

const MoBox = motion(Box);

export default function MarketingMobileVisual() {
  const theme = useTheme();
  const dark = theme.palette.mode === "dark";
  const reduce = useReducedMotion();
  const [ref, inView] = useInViewOnce();
  const green = theme.palette.primary.main;
  const rev = useCountUp(DEMO_METRICS.revenueToday, { active: inView });

  const [notify, setNotify] = useState(false);
  useEffect(() => {
    if (!inView || reduce) return;
    const t = setTimeout(() => setNotify(true), 1200);
    return () => clearTimeout(t);
  }, [inView, reduce]);

  const symbol = dark ? "/pesafii-symbol-white-green.svg" : "/pesafii-symbol-black-green.svg";

  return (
    <Box ref={ref} sx={{ position: "relative" }}>
      <MarketingMobileFrame>
        {/* header */}
        <Box sx={{ px: 1.5, py: 1.25, display: "flex", alignItems: "center", justifyContent: "space-between", borderBottom: "1px solid", borderColor: "divider", bgcolor: "action.hover" }}>
          <Stack direction="row" spacing={0.75} alignItems="center">
            <Box component="img" src={symbol} alt="" sx={{ width: 18, height: 18 }} />
            <Typography sx={{ fontSize: 12.5, fontWeight: 800, color: "text.primary" }}>PesaFii</Typography>
          </Stack>
          <Box sx={{ width: 22, height: 22, borderRadius: "50%", bgcolor: alpha(green, 0.15), display: "flex", alignItems: "center", justifyContent: "center" }}>
            <Box sx={{ width: 9, height: 9, borderRadius: "50%", bgcolor: green }} />
          </Box>
        </Box>

        <Box sx={{ p: 1.5 }}>
          {/* revenue */}
          <Box sx={{ p: 1.5, borderRadius: 2, bgcolor: alpha(green, dark ? 0.08 : 0.06), border: "1px solid", borderColor: alpha(green, 0.2), mb: 1.25 }}>
            <Typography sx={{ fontSize: 10.5, color: "text.secondary", fontWeight: 600 }}>Revenue today</Typography>
            <Typography sx={{ fontSize: 20, fontWeight: 800, color: "text.primary", lineHeight: 1.1 }}>UGX {Math.round(rev).toLocaleString()}</Typography>
            <Box sx={{ mt: 0.75 }}><MarketingMiniBars data={DEMO_WALLET.activity} active={inView} color={green} height={34} /></Box>
          </Box>

          {/* two stats */}
          <Stack direction="row" spacing={1} sx={{ mb: 1.25 }}>
            <Box sx={{ flex: 1, p: 1.25, borderRadius: 2, border: "1px solid", borderColor: "divider" }}>
              <Typography sx={{ fontSize: 10, color: "text.secondary", fontWeight: 600 }}>Routers</Typography>
              <Typography sx={{ fontSize: 15, fontWeight: 800, color: "text.primary" }}>{DEMO_METRICS.onlineRouters}/{DEMO_METRICS.connectedRouters}</Typography>
            </Box>
            <Box sx={{ flex: 1, p: 1.25, borderRadius: 2, border: "1px solid", borderColor: "divider" }}>
              <Typography sx={{ fontSize: 10, color: "text.secondary", fontWeight: 600 }}>Active users</Typography>
              <Typography sx={{ fontSize: 15, fontWeight: 800, color: "text.primary" }}>{DEMO_METRICS.activeUsers}</Typography>
            </Box>
          </Stack>

          {/* router status */}
          <Typography sx={{ fontSize: 10.5, fontWeight: 700, color: "text.secondary", mb: 0.5 }}>Routers</Typography>
          <Stack spacing={0.75} sx={{ mb: 1.25 }} divider={<Divider sx={{ borderColor: "divider" }} />}>
            {DEMO_LOCATIONS.slice(0, 2).map((r) => (
              <Stack key={r.id} direction="row" justifyContent="space-between" alignItems="center">
                <Typography noWrap sx={{ fontSize: 11, color: "text.primary", fontWeight: 600, minWidth: 0 }}>{r.name}</Typography>
                <MarketingStatusBadge status={r.online ? "online" : "offline"} />
              </Stack>
            ))}
          </Stack>

          {/* recent tx */}
          <Typography sx={{ fontSize: 10.5, fontWeight: 700, color: "text.secondary", mb: 0.5 }}>Recent</Typography>
          <Stack direction="row" justifyContent="space-between" alignItems="center">
            <Typography sx={{ fontSize: 11, color: "text.primary", fontWeight: 600 }}>{DEMO_TRANSACTIONS[0].customer}</Typography>
            <Typography sx={{ fontSize: 11, fontWeight: 700, color: "text.primary" }}>{fmtUGX(DEMO_TRANSACTIONS[0].amount)}</Typography>
          </Stack>
        </Box>
      </MarketingMobileFrame>

      {/* slide-in notification */}
      {!reduce && (
        <MoBox
          initial={{ opacity: 0, y: 12, scale: 0.9 }}
          animate={notify ? { opacity: 1, y: 0, scale: 1 } : { opacity: 0, y: 12, scale: 0.9 }}
          transition={{ duration: 0.45, ease: [0.22, 1, 0.36, 1] }}
          sx={{
            position: "absolute", bottom: 14, left: "50%", transform: "translateX(-50%)",
            width: 220, maxWidth: "88%", px: 1.25, py: 1, borderRadius: 2,
            bgcolor: "background.paper", border: "1px solid", borderColor: alpha(green, 0.3),
            boxShadow: dark ? "0 12px 30px rgba(0,0,0,0.5)" : "0 12px 30px rgba(15,23,42,0.16)",
            display: "flex", alignItems: "center", gap: 1,
          }}
        >
          <CheckCircleIcon sx={{ fontSize: 18, color: green }} />
          <Box sx={{ minWidth: 0 }}>
            <Typography sx={{ fontSize: 11, fontWeight: 700, color: "text.primary" }}>Payment received</Typography>
            <Typography sx={{ fontSize: 10, color: "text.secondary" }}>{fmtUGX(2000)} · voucher redeemed</Typography>
          </Box>
        </MoBox>
      )}
    </Box>
  );
}
